<template>
  <div class="monitor-result">
    <h2>任务组管理</h2>
    
    <!-- 操作按钮区域 -->
    <div class="action-buttons">
      <el-button type="primary" @click="createTaskGroup">
        <el-icon><Plus /></el-icon>
        创建任务组
      </el-button>
      <el-button 
        type="danger" 
        :disabled="selectedTaskGroups.length === 0" 
        @click="batchDeleteTaskGroups"
      >
        <el-icon><Delete /></el-icon>
        批量删除 ({{ selectedTaskGroups.length }})
      </el-button>
      <el-button type="info" @click="refreshData">
        <el-icon><Refresh /></el-icon>
        刷新数据
      </el-button>
    </div>
    
    <!-- 搜索和筛选区域 -->
    <div class="filter-section">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-input
            v-model="searchQuery"
            placeholder="搜索任务组ID"
            clearable
            @clear="handleSearch"
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-col>
        <el-col :span="8">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="handleDateChange"
          />
        </el-col>
        <el-col :span="8">
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetFilters">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 任务组卡片区域 -->
    <div class="task-groups-section">
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="6" animated />
      </div>
      
      <div v-else-if="taskGroups.length === 0" class="empty-state">
        <el-empty description="暂无任务组数据" />
      </div>
      
      <div v-else class="task-groups-grid">
        <div 
          v-for="group in taskGroups" 
          :key="group.id || group.taskid"
          class="task-group-card"
          :class="{ 'selected': isTaskGroupSelected(group) }"
          @click="toggleTaskGroupSelection(group)"
        >
          <div class="card-header">
            <div class="task-id">
              <el-checkbox 
                :model-value="isTaskGroupSelected(group)" 
                @click.stop
                @change="toggleTaskGroupSelection(group)"
              />
              <el-tag type="primary" size="small">ID: {{ group.taskid || '未知' }}</el-tag>
            </div>
            <div class="card-actions">
              <el-dropdown 
                trigger="click" 
                @command="handleCardAction"
                @click.stop
              >
                <el-button 
                  type="primary" 
                  size="small" 
                  class="action-button"
                >
                  操作功能
                  <el-icon><More /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item :command="{ action: 'view', group }">
                      <el-icon><View /></el-icon>
                      查看详情
                    </el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'download', group }">
                      <el-icon><Download /></el-icon>
                      下载Excel
                    </el-dropdown-item>
                    <el-dropdown-item :command="{ action: 'reExecute', group }">
                      <el-icon><Refresh /></el-icon>
                      重新执行
                    </el-dropdown-item>
                    <el-dropdown-item 
                      :command="{ action: 'delete', group }" 
                      style="color: #F56C6C;"
                    >
                      <el-icon><Delete /></el-icon>
                      删除
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
          
          <div class="card-content">
            <div class="stats-row">
              <div class="stat-item">
                <div class="stat-label">任务总数</div>
                <div class="stat-value total">{{ group.total_tasks || 0 }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">成功数</div>
                <div class="stat-value success">{{ group.success_count || 0 }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">失败数</div>
                <div class="stat-value failed">{{ group.failed_count || 0 }}</div>
              </div>
            </div>
            
            <div class="progress-row">
              <div class="progress-info">
                <span>完成进度</span>
                <span class="progress-percent">
                  {{ calculateCompletionRate(group) }}%
                </span>
              </div>
              <el-progress 
                :percentage="calculateCompletionRate(group)" 
                :stroke-width="6"
                :color="getProgressColor(calculateCompletionRate(group))"
              />
            </div>
            
            <div class="time-info">
              <el-icon><Clock /></el-icon>
              <span class="time-text">创建时间: {{ formatTime(group.create_time || group.createtime) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[6, 12, 24, 48]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 创建任务组对话框 -->
    <el-dialog
      v-model="showCreateDialog"
      title="创建任务组"
      width="500px"
      :before-close="closeCreateDialog"
    >
      <div class="create-dialog-content">
    
        
        <div class="upload-section">
          <el-upload
            class="upload-demo"
            drag
            :auto-upload="false"
            :on-change="handleFileUpload"
            :show-file-list="false"
            accept=".xlsx,.xls"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              拖拽Excel文件到此处或 <em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                仅支持 .xlsx, .xls 格式文件
              </div>
            </template>
          </el-upload>
              <!-- 下载模板按钮 -->
        <div class="template-section" style="margin-top: 20px;">
          <el-button 
            type="success" 
            @click="downloadTemplate"
            class="template-button"
          >
            <el-icon><Download /></el-icon>
            下载导入模板
          </el-button>
          <p class="template-tip">下载Excel模板文件，按照模板格式填写数据后上传</p>
        </div>
          <div v-if="fileName" class="file-info">
            <el-icon><Document /></el-icon>
            <span class="file-name">{{ fileName }}</span>
            <span v-if="rowCount > 0" class="row-count">({{ rowCount }} 行数据)</span>
          </div>
        </div>
        
        <div class="submit-section">
          <el-button 
            type="primary" 
            :disabled="!uploadFile" 
            :loading="submitLoading"
            @click="submitExcel"
          >
            <el-icon><Upload /></el-icon>
            确认提交
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 任务详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      :title="`任务组详情 - ${selectedGroup?.taskid || ''}`"
      width="90%"
      top="5vh"
    >
      <div v-if="selectedGroup" class="detail-content">
        <div class="detail-stats">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="detail-stat-card">
                <div class="detail-stat-icon total">
                  <el-icon><Files /></el-icon>
                </div>
                <div class="detail-stat-content">
                  <h3>任务总数</h3>
                  <p class="detail-value">{{ selectedGroup.total_tasks || 0 }}</p>
                </div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="detail-stat-card">
                <div class="detail-stat-icon success">
                  <el-icon><CircleCheck /></el-icon>
                </div>
                <div class="detail-stat-content">
                  <h3>成功数</h3>
                  <p class="detail-value">{{ selectedGroup.success_count || 0 }}</p>
                </div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="detail-stat-card">
                <div class="detail-stat-icon failed">
                  <el-icon><CircleClose /></el-icon>
                </div>
                <div class="detail-stat-content">
                  <h3>失败数</h3>
                  <p class="detail-value">{{ selectedGroup.failed_count || 0 }}</p>
                </div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="detail-stat-card">
                <div class="detail-stat-icon rate">
                  <el-icon><TrendCharts /></el-icon>
                </div>
                <div class="detail-stat-content">
                  <h3>完成进度</h3>
                  <p class="detail-value">{{ calculateCompletionRate(selectedGroup) }}%</p>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 任务详情表格 -->
        <div class="detail-table-section">
          <div class="table-header">
            <h3>任务详情</h3>
            <el-button type="primary" size="small" @click="exportGroupTasks(selectedGroup)">
              <el-icon><Download /></el-icon>
              导出本组任务
            </el-button>
          </div>
          
          <!-- 使用v-if避免ResizeObserver错误 -->
          <el-table
            v-if="detailDialogVisible"
            :data="groupTasks"
            style="width: 100%"
            stripe
            v-loading="detailLoading"
            :default-sort="{ prop: 'id', order: 'descending' }"
            key="task-detail-table"
          >
            <el-table-column prop="id" label="编号" width="80" sortable />
            <el-table-column prop="taskid" label="任务ID" width="120" />
            <el-table-column prop="input_url" label="原链接" >
              <template #default="{ row }">
                <a :href="row.input_url" target="_blank" class="url-link">{{ truncateText(row.input_url, 30) }}</a>
              </template>
            </el-table-column>
             <!-- 视频平台列 -->
            <el-table-column prop="platform" label="视频平台" width="120">
              <template #default="{ row }">
                <span class="platform-text">{{ row.platform || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="title" label="标题" min-width="180">
              <template #default="{ row }">
                <div class="title-text">{{ row.title || '无标题' }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="author" label="作者" width="120">
              <template #default="{ row }">
                <div class="author-cell">
                  <el-avatar :size="24" :src="row.avatar" v-if="row.avatar" />
                  <span class="author-name">{{ row.author || '未知' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="发布时间" width="140" sortable>
              <template #default="{ row }">
                {{ formatTime(row.create_time) }}
              </template>
            </el-table-column>
            <el-table-column prop="digg" label="点赞" width="80" sortable />
            <el-table-column prop="comment" label="收藏" width="80" sortable />
           
           
            <el-table-column prop="state" label="解析状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getStateTagType(row.state)" size="small">
                  {{ getStateText(row.state) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createtime" label="创建时间"  sortable>
              <template #default="{ row }">
                {{ formatTime(row.createtime) }}
              </template>
            </el-table-column>
             <!-- 更新时间列 -->
            <el-table-column prop="updatetime" label="更新时间"  sortable>
              <template #default="{ row }">
                {{ formatTime(row.updatetime) || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" width="150">
              <template #default="{ row }">
                <span class="remark-text">{{ row.remark || '-' }}</span>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 任务详情分页 -->
          <div class="detail-pagination">
            <el-pagination
              v-model:current-page="detailCurrentPage"
              v-model:page-size="detailPageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="detailTotal"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleDetailSizeChange"
              @current-change="handleDetailPageChange"
            />
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 导出对话框 -->
    <el-dialog
      v-model="exportDialogVisible"
      :title="exportType === 'download' ? '下载Excel' : '导出本组任务'"
      width="500px"
      :before-close="closeExportDialog"
    >
      <div class="export-dialog-content">
        <div class="export-form">
          <el-form label-width="100px">
            <el-form-item label="任务组ID">
              <el-input 
                :value="exportGroup ? exportGroup.taskid : ''" 
                disabled 
                placeholder="任务组ID"
              />
            </el-form-item>
            <el-form-item label="导出类型">
              <el-select 
                v-model="exportState" 
                placeholder="请选择导出类型"
                clearable
                style="width: 100%"
              >
                <el-option label="全部" value="" />
                <el-option label="待解析" value="0" />
                <el-option label="解析成功" value="1" />
                <el-option label="解析失败" value="2" />
              </el-select>
            </el-form-item>
          </el-form>
        </div>
        
        <div class="export-actions">
          <el-button @click="closeExportDialog">关闭</el-button>
          <el-button 
            type="primary" 
            :loading="exportLoading"
            @click="executeExport"
          >
            导出
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import {
  Plus,
  Refresh,
  Search,
  Download,
  Clock,
  Files,
  CircleCheck,
  CircleClose,
  TrendCharts,
  UploadFilled,
  Document,
  Upload,
  Delete,
  More,
  View
} from '@element-plus/icons-vue'

export default {
  name: 'MonitorResultView',
  components: {
    Plus,
    Refresh,
    Search,
    Download,
    Clock,
    Files,
    CircleCheck,
    CircleClose,
    TrendCharts,
    UploadFilled,
    Document,
    Upload,
    Delete,
    More,
    View
  },
  data() {
    return {
      searchQuery: '',
      dateRange: [],
      loading: false,
      currentPage: 1,
      pageSize: 6,
      total: 0,
      taskGroups: [],
      
      // 创建任务组对话框相关
      showCreateDialog: false,
      uploadFile: null,
      fileName: '',
      rowCount: 0,
      submitLoading: false,
      
      // 多选相关
      selectedTaskGroups: [],
      
      // 详情对话框相关
      detailDialogVisible: false,
      selectedGroup: null,
      groupTasks: [],
      detailLoading: false,
      detailCurrentPage: 1,
      detailPageSize: 10,
      detailTotal: 0,
      
      // 导出对话框相关
      exportDialogVisible: false,
      exportType: 'download', // 'download' 或 'export'
      exportGroup: null,
      exportState: '',
      exportLoading: false
    }
  },
  created() {
    this.fetchTaskGroups()
  },
  methods: {
    async fetchTaskGroups() {
      this.loading = true
      try {
        // 构建查询参数
        const params = {
          page: this.currentPage,
          pagesize: this.pageSize
        }
        
        // 添加搜索查询
        if (this.searchQuery) {
          params.taskid = this.searchQuery
        }
        
        // 添加日期筛选
        if (this.dateRange && this.dateRange.length === 2) {
          const [startDate, endDate] = this.dateRange
          params.start_date = this.formatDateForAPI(startDate)
          params.end_date = this.formatDateForAPI(endDate)
        }
        
        // 调用API获取任务组数据
        const response = await this.$axios.get('api/task/taskGroups', { params })
        
        // 根据用户提供的API响应结构，code=1表示成功
        if (response.code !== 1) {
          this.$message.error(response.msg || '获取数据失败')
          return
        }
        
        const data = response.data || {}
        
        // 更新数据，假设API返回的数据结构为 { list: [], total: 0 }
        const list = data.list || data.data || []
        this.total = data.total || list.length
        
        this.taskGroups = list.map(item => ({
          id: item.id || 0,
          taskid: item.taskid || '',
          total_tasks: item.total_tasks || item.total_count || 0,
          success_count: item.success_count || 0,
          failed_count: item.failed_count || 0,
          create_time: item.create_time || item.createtime || 0,
          remark: item.remark || ''
        }))
        
      } catch (error) {
        console.error('获取任务组数据失败:', error)
        this.$message.error('获取数据失败')
        this.taskGroups = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    
    async fetchGroupTasks(group) {
      this.detailLoading = true
      try {
        const params = {
          page: this.detailCurrentPage,
          pagesize: this.detailPageSize,
          taskid: group.taskid
        }
        
        const response = await this.$axios.get('api/task/delite', { params })
        
        if (response.code !== 1) {
          this.$message.error(response.msg || '获取任务详情失败')
          return
        }
        
        const data = response.data || {}
        const list = data.list || data.data || []
        this.detailTotal = data.total || list.length
        
        this.groupTasks = list.map(item => ({
          id: item.id || 0,
          taskid: item.taskid || '',
          input_url: item.input_url || '',
          title: item.title || '',
          author: item.author || '',
          avatar: item.avatar || '',
          create_time: item.create_time || 0,
          digg: item.digg || 0,
          comment: item.comment || 0,
          collect: item.collect || 0,
          share_count: item.share_count || 0,
          follower: item.follower || '',
          total_favorited: item.total_favorited || '',
          cover: item.cover || '',
          url: item.url || '',
          createtime: item.createtime || 0,
           platform: item.platform || '',
            updatetime: item.updatetime || '',
          state: item.state || '0',
          remark: item.remark || ''
        }))
        
      } catch (error) {
        console.error('获取任务详情失败:', error)
        this.$message.error('获取任务详情失败')
        this.groupTasks = []
        this.detailTotal = 0
      } finally {
        this.detailLoading = false
      }
    },
    
    // 创建任务组相关方法
    createTaskGroup() {
      this.showCreateDialog = true
      this.uploadFile = null
      this.fileName = ''
      this.rowCount = 0
    },
    
    // 处理文件上传
    handleFileUpload(file) {
      this.uploadFile = file.raw
      this.fileName = file.name
      
      // 读取Excel文件获取行数
      this.readExcelFile(file.raw)
    },
    
    // 读取Excel文件
    readExcelFile(file) {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        try {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary' })
          
          // 获取第一个工作表
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          
          // 将工作表转换为JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
          
          // 计算实际数据行数（排除空行和表头）
          let rowCount = 0
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i]
            // 检查行是否有数据（至少有一个非空单元格）
            if (row && row.some(cell => cell !== null && cell !== undefined && cell !== '')) {
              rowCount++
            }
          }
          
          // 如果第一行是表头，减去表头行
          if (rowCount > 0) {
            rowCount = rowCount - 1 // 减去表头行
          }
          
          this.rowCount = rowCount > 0 ? rowCount : 0
          
          if (this.rowCount > 0) {
            this.$message.success(`文件 "${this.fileName}" 读取成功，共 ${this.rowCount} 行数据`)
          } else {
            this.$message.warning('Excel文件中没有找到有效数据')
          }
          
        } catch (error) {
          console.error('读取Excel文件失败:', error)
          this.$message.error('读取Excel文件失败，请检查文件格式')
          this.rowCount = 0
        }
      }
      
      reader.onerror = () => {
        this.$message.error('读取文件失败')
        this.rowCount = 0
      }
      
      reader.readAsBinaryString(file)
    },
    
    // 提交Excel文件
    submitExcel() {
      if (!this.uploadFile) {
        this.$message.warning('请先上传Excel文件')
        return
      }
      
      this.$confirm(`确定要提交 ${this.rowCount} 行数据吗？`, '确认提交', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.submitToAPI()
      }).catch(() => {})
    },
    
    // 提交到API
    async submitToAPI() {
      this.submitLoading = true
      try {
        const formData = new FormData()
        formData.append('file', this.uploadFile)
        
        // 调用API提交Excel
        const response = await this.$axios.post('api/jx/importExcel', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.code !== 1) {
          this.$message.error(response.msg || '提交失败')
          return
        }
        
        // 获取第一次响应中的 location
        let location = response.location || response.data?.location
        
        // 如果存在 location，则再次访问该 location 获取真实数据
        if (location) {
          try {
            // 第二次访问，获取 location 中的数据
            const secondResponse = await this.$axios.get(location)
            
            // 从第二次响应中提取 location
            const finalLocation = secondResponse.location || secondResponse.data?.location
            
            console.log('第一次 location:', location)
            console.log('第二次 location:', finalLocation)
            
            // 这里可以使用 finalLocation 进行后续操作
            // 例如：下载、保存等
            
          } catch (locationError) {
            console.error('获取location数据失败:', locationError)
            this.$message.warning('获取任务数据失败，但文件已提交')
          }
        }
        
        this.$message.success('Excel文件提交成功')
        this.showCreateDialog = false
        this.uploadFile = null
        this.fileName = ''
        this.rowCount = 0
        
        // 刷新任务组数据
        this.fetchTaskGroups()
        
      } catch (error) {
        console.error('提交Excel失败:', error)
        this.$message.error('提交失败')
      } finally {
        this.submitLoading = false
      }
    },
    
    // 关闭创建对话框
    closeCreateDialog() {
      this.showCreateDialog = false
      this.uploadFile = null
      this.fileName = ''
      this.rowCount = 0
    },
    
    viewGroupDetails(group) {
      this.selectedGroup = group
      this.detailDialogVisible = true
      this.detailCurrentPage = 1
      this.fetchGroupTasks(group)
    },
    
    // 计算完成进度（已处理任务数/总任务数）
    calculateCompletionRate(group) {
      if (!group.total_tasks || group.total_tasks === 0) return 0
      const processedTasks = (group.success_count || 0) + (group.failed_count || 0)
      const completionRate = (processedTasks / group.total_tasks) * 100
      return Math.min(Math.round(completionRate * 100) / 100, 100) // 保留两位小数，不超过100%
    },
    
    getProgressColor(percentage) {
      if (percentage >= 90) return '#67C23A'
      if (percentage >= 70) return '#E6A23C'
      return '#F56C6C'
    },
    
    handleSearch() {
      this.currentPage = 1
      this.fetchTaskGroups()
    },
    
    handleDateChange() {
      this.currentPage = 1
      this.fetchTaskGroups()
    },
    
    resetFilters() {
      this.searchQuery = ''
      this.dateRange = []
      this.currentPage = 1
      this.fetchTaskGroups()
    },
    
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchTaskGroups()
    },
    
    handlePageChange(page) {
      this.currentPage = page
      this.fetchTaskGroups()
    },
    
    handleDetailSizeChange(size) {
      this.detailPageSize = size
      this.detailCurrentPage = 1
      if (this.selectedGroup) {
        this.fetchGroupTasks(this.selectedGroup)
      }
    },
    
    handleDetailPageChange(page) {
      this.detailCurrentPage = page
      if (this.selectedGroup) {
        this.fetchGroupTasks(this.selectedGroup)
      }
    },
    
    getStateTagType(state) {
      const map = {
        '0': 'info',      // 待处理
        '1': 'success',   // 处理成功
        '2': 'danger'     // 处理失败
      }
      return map[state] || 'info'
    },
    
    getStateText(state) {
      const map = {
        '0': '待处理',
        '1': '处理成功',
        '2': '处理失败'
      }
      return map[state] || '未知'
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    // 格式化日期为API需要的格式 (YYYY-MM-DD)
    formatDateForAPI(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    
    // 下载Excel
    downloadExcel(group) {
      this.showExportDialog('download', group)
    },
    
    // 切换任务组选择
    toggleTaskGroupSelection(group) {
      const index = this.selectedTaskGroups.findIndex(g => 
        (g.id && g.id === group.id) || (g.taskid && g.taskid === group.taskid)
      )
      
      if (index === -1) {
        this.selectedTaskGroups.push(group)
      } else {
        this.selectedTaskGroups.splice(index, 1)
      }
    },
    
    // 检查任务组是否被选中
    isTaskGroupSelected(group) {
      return this.selectedTaskGroups.some(g => 
        (g.id && g.id === group.id) || (g.taskid && g.taskid === group.taskid)
      )
    },
    
    // 批量删除任务组
    async batchDeleteTaskGroups() {
      if (this.selectedTaskGroups.length === 0) {
        this.$message.warning('请先选择要删除的任务组')
        return
      }
      
      const taskIds = this.selectedTaskGroups.map(g => g.taskid).join(',')
      this.$confirm(`确定要删除选中的 ${this.selectedTaskGroups.length} 个任务组吗？此操作不可恢复！`, '批量删除任务组', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'error'
      }).then(async () => {
        try {
          // 调用API批量删除任务组
          const response = await this.$axios.post('api/task/deletetask', {
            taskid: taskIds
          })
          
          if (response.code !== 1) {
            this.$message.error(response.msg || '删除失败')
            return
          }
          
          this.$message.success(`成功删除 ${this.selectedTaskGroups.length} 个任务组`)
          // 删除后刷新数据并清空选择
          this.selectedTaskGroups = []
          this.fetchTaskGroups()
          
        } catch (error) {
          console.error('批量删除任务组失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    
    // 删除单个任务组
    async deleteTaskGroup(group) {
      this.$confirm(`确定要删除任务组 "${group.taskid}" 吗？此操作不可恢复！`, '删除任务组', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'error'
      }).then(async () => {
        try {
          // 调用API删除单个任务组
          const response = await this.$axios.post('api/task/deletetask', {
            taskid: group.taskid
          })
          
          if (response.code !== 1) {
            this.$message.error(response.msg || '删除失败')
            return
          }
          
          this.$message.success('任务组删除成功')
          // 删除后刷新数据
          this.fetchTaskGroups()
          // 如果该任务组被选中，从选中列表中移除
          const index = this.selectedTaskGroups.findIndex(g => 
            (g.id && g.id === group.id) || (g.taskid && g.taskid === group.taskid)
          )
          if (index !== -1) {
            this.selectedTaskGroups.splice(index, 1)
          }
          
        } catch (error) {
          console.error('删除任务组失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    
    // 处理卡片操作
    handleCardAction(command) {
      const { action, group } = command
      
      switch (action) {
        case 'view':
          this.viewGroupDetails(group)
          break
        case 'download':
          this.downloadExcel(group)
          break
        case 'reExecute':
          this.reExecuteTaskGroup(group)
          break
        case 'delete':
          this.deleteTaskGroup(group)
          break
      }
    },
    
    // 重新执行任务组
    reExecuteTaskGroup(group) {
      this.$confirm(`确定要重新执行任务组 "${group.taskid}" 吗？`, '重新执行任务组', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 调用API重新执行任务组 - 改为使用 /api/v1/executeTaskReal
          const response = await this.$axios.post('api/v1/asyncExecuteTask', {
            taskid: group.taskid
          })
          
          if (response.code !== 1) {
            this.$message.error(response.msg || '重新执行失败')
            return
          }
          
          this.$message.success('任务组已提交重新执行')
          // 刷新数据
          this.fetchTaskGroups()
          
        } catch (error) {
          console.error('重新执行任务组失败:', error)
          this.$message.error('重新执行失败')
        }
      }).catch(() => {})
    },
    
    // 导出本组任务
    exportGroupTasks(group) {
      this.showExportDialog('export', group)
    },
    
    // 显示导出对话框
    showExportDialog(type, group) {
      this.exportType = type
      this.exportGroup = group
      this.exportState = ''
      this.exportDialogVisible = true
    },
    
    // 关闭导出对话框
    closeExportDialog() {
      this.exportDialogVisible = false
      this.exportGroup = null
      this.exportState = ''
      this.exportLoading = false
    },
    
    // 执行导出
    async executeExport() {
      if (!this.exportGroup) return
      
      this.exportLoading = true
      try {
        // 构建请求参数
        const params = {
          taskid: this.exportGroup.taskid
        }
        
        // 如果有选择导出类型，添加state参数
        if (this.exportState) {
          params.state = this.exportState
        }
        
        // 调用API导出任务
        const response = await this.$axios.get('/api/v1/exportTask', { params })
        
        if (response.code !== 1) {
          this.$message.error(response.msg || '导出失败')
          return
        }
        
        const title = this.exportType === 'download' ? '下载Excel' : '导出本组任务'
        this.$message.success(`${title}成功`)
        
        // 检查响应是否包含下载URL
        if (response.data && response.data.download_url) {
          // 自动触发文件下载
          this.triggerFileDownload(response.data.download_url, response.data.filename)
        }
        
        // 关闭对话框
        this.closeExportDialog()
        
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      } finally {
        this.exportLoading = false
      }
    },
    
    // 触发文件下载
    triggerFileDownload(downloadUrl, filename = '') {
      if (!downloadUrl) return
      
      // 方法1：方法1：使用代理下载（推荐）
      const proxyUrl = downloadUrl
      
      // 创建隐藏的iframe下载（不会打开新窗口）
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = proxyUrl
      document.body.appendChild(iframe)
      
      // 同时使用a标签下载（不设置target，在当前窗口下载）
      const link = document.createElement('a')
      link.href = proxyUrl
      link.download = filename || 'download.xlsx'
      // 不设置target，让浏览器在当前窗口下载
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // 清理iframe
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe)
        }
      }, 5000)
      
      this.$message.info('文件下载中...')
    },
    
    // 下载模板
    downloadTemplate() {
      const templateUrl = '/创建模板.xlsx'
      const link = document.createElement('a')
      link.href = templateUrl
      link.download = '创建模板.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$message.success('模板下载开始')
    },
    
    // 刷新数据
    refreshData() {
      this.fetchTaskGroups()
      this.selectedTaskGroups = [] // 刷新时清空选择
      this.$message.success('数据刷新成功')
    }
  }
}
</script>

<style scoped>
.monitor-result {
  padding: 20px;
}

.action-buttons {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.filter-section {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.task-groups-section {
  margin-bottom: 20px;
}

.loading-container {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.empty-state {
  background: #fff;
  padding: 40px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  text-align: center;
}

.task-groups-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

@media (max-width: 1200px) {
  .task-groups-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .task-groups-grid {
    grid-template-columns: 1fr;
  }
}

.task-group-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
  border: 2px solid #ebeef5;
  cursor: pointer;
  position: relative;
}

.task-group-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
}

.task-group-card.selected {
  border-color: #409EFF;
  background-color: #f0f7ff;
}

.task-group-card.selected::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #409EFF;
  border-radius: 8px 8px 0 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.task-id {
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  border: 1px solid #409EFF;
  border-radius: 4px;
  padding: 4px 12px;
  background-color: #fff;
  color: #409EFF;
  transition: all 0.3s;
}

.action-button:hover {
  background-color: #409EFF;
  color: #fff;
  border-color: #409EFF;
}

.action-button .el-icon {
  margin-right: 4px;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.stat-item {
  flex: 1;
  text-align: center;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 6px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
}

.stat-value.total {
  color: #409EFF;
}

.stat-value.success {
  color: #67C23A;
}

.stat-value.failed {
  color: #F56C6C;
}

.progress-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-percent {
  font-weight: bold;
  color: #409EFF;
}

.time-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.time-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 创建对话框样式 */
.create-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-section {
  text-align: center;
}

.file-info {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #409EFF;
}

.file-name {
  font-weight: bold;
}

.row-count {
  color: #67C23A;
  font-weight: bold;
}

.submit-section {
  text-align: center;
}

/* 详情对话框样式 */
.detail-content {
  max-height: 70vh;
  overflow-y: auto;
}

.detail-stats {
  margin-bottom: 20px;
}

.detail-stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);
}

.detail-stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
  color: white;
}

.detail-stat-icon.total {
  background: linear-gradient(135deg, #409EFF, #66B1FF);
}

.detail-stat-icon.success {
  background: linear-gradient(135deg, #67C23A, #85CE61);
}

.detail-stat-icon.failed {
  background: linear-gradient(135deg, #F56C6C, #F78989);
}

.detail-stat-icon.rate {
  background: linear-gradient(135deg, #E6A23C, #F2C94C);
}

.detail-stat-content h3 {
  margin: 0 0 5px 0;
  color: #606266;
  font-size: 14px;
  font-weight: normal;
}

.detail-stat-content .detail-value {
  font-size: 20px;
  font-weight: bold;
  margin: 0;
  color: #303133;
}

.detail-table-section {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.table-header h3 {
  margin: 0;
  color: #303133;
}

.detail-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 表格相关样式 */
.author-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.author-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.url-link {
  color: #409EFF;
  text-decoration: none;
}

.url-link:hover {
  text-decoration: underline;
}

.remark-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 导出对话框样式 */
.export-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.export-form {
  margin-bottom: 20px;
}

.export-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
